// src/components/TerminalControls.js
import React, { useState, useEffect } from 'react';

const TerminalControls = ({ 
  isRunning, 
  isPaused, 
  onStop, 
  onStart, 
  onPause, 
  onResume,
  strategyId,
  agentId  // Add agentId prop to listen for auto-stop events
}) => {
  const [loading, setLoading] = useState(false);
  const [lastActionType, setLastActionType] = useState(null);

  // Listen for auto-stop events to update loading state
  useEffect(() => {
    const handleAutoStop = (event) => {
      const { agentId: eventAgentId, strategyId: eventStrategyId } = event.detail;
      
      // Check if this event is for our current agent/strategy
      if (agentId && eventAgentId === agentId.toString()) {
        console.log(`[TerminalControls] Received auto-stop event for agent ${eventAgentId}`);
        
        // Clear any loading states since strategy was auto-stopped
        setLoading(false);
        setLastActionType('auto-stop');
        
        // Small delay to ensure parent components update first
        setTimeout(() => {
          setLastActionType(null);
        }, 1000);
      }
    };
    
    // Add event listener
    window.addEventListener('strategy-auto-stopped', handleAutoStop);
    
    // Cleanup
    return () => {
      window.removeEventListener('strategy-auto-stopped', handleAutoStop);
    };
  }, [agentId]);

  // Wrapper functions to handle loading state
  const handleStop = async () => {
    try {
      setLoading(true);
      setLastActionType('stop');
      await onStop();
    } catch (error) {
      console.error('Stop action error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    try {
      setLoading(true);
      setLastActionType('start');
      await onStart();
    } catch (error) {
      console.error('Start action error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async () => {
    try {
      setLoading(true);
      setLastActionType('pause');
      await onPause();
    } catch (error) {
      console.error('Pause action error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async () => {
    try {
      setLoading(true);
      setLastActionType('resume');
      await onResume();
    } catch (error) {
      console.error('Resume action error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-2 sm:p-3 border-b border-gray-800 bg-gray-900 text-white rounded-t-lg space-y-2 sm:space-y-0">
      <div className="flex items-center space-x-2 min-w-0">
        <span className="font-medium text-sm sm:text-base">Terminal</span>
        {strategyId && (
          <span className="text-xs bg-gray-700 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded truncate max-w-[120px] sm:max-w-none">
            <span className="hidden sm:inline">Strategy ID: </span>{strategyId}
          </span>
        )}
        <span className={`flex items-center text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 rounded flex-shrink-0 ${
          isRunning
            ? isPaused
              ? 'bg-yellow-900 text-yellow-400'
              : 'bg-green-900 text-green-400'
            : 'bg-gray-700 text-gray-400'
        }`}>
          <span className={`w-2 h-2 mr-1 rounded-full ${
            isRunning
              ? isPaused
                ? 'bg-yellow-400'
                : 'bg-green-400 animate-pulse'
              : 'bg-gray-400'
          }`}></span>
          {loading ? (
            <>
              <span className="hidden sm:inline">Processing...</span>
              <span className="sm:hidden">...</span>
            </>
          ) : (
            isRunning
              ? isPaused
                ? 'Paused'
                : 'Running'
              : 'Stopped'
          )}
        </span>
      </div>
      <div className="flex space-x-1 sm:space-x-2 justify-end sm:justify-start">
        {isRunning ? (
          <>
            <button
              onClick={handleStop}
              disabled={loading}
              className={`px-2 sm:px-3 py-1 text-white rounded transition-colors text-xs sm:text-sm font-medium min-w-[60px] sm:min-w-[70px] ${
                loading && lastActionType === 'stop'
                  ? 'bg-gray-500 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700'
              }`}
            >
              {loading && lastActionType === 'stop' ? (
                <>
                  <span className="hidden sm:inline">Stopping...</span>
                  <span className="sm:hidden">Stop...</span>
                </>
              ) : (
                'Stop'
              )}
            </button>
            {isPaused ? (
              <button
                onClick={handleResume}
                disabled={loading}
                className={`px-2 sm:px-3 py-1 text-white rounded transition-colors text-xs sm:text-sm font-medium min-w-[60px] sm:min-w-[80px] ${
                  loading && lastActionType === 'resume'
                    ? 'bg-gray-500 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-700'
                }`}
              >
                {loading && lastActionType === 'resume' ? (
                  <>
                    <span className="hidden sm:inline">Resuming...</span>
                    <span className="sm:hidden">Resume...</span>
                  </>
                ) : (
                  'Resume'
                )}
              </button>
            ) : (
              <button
                onClick={handlePause}
                disabled={loading}
                className={`px-2 sm:px-3 py-1 text-white rounded transition-colors text-xs sm:text-sm font-medium min-w-[60px] sm:min-w-[70px] ${
                  loading && lastActionType === 'pause'
                    ? 'bg-gray-500 cursor-not-allowed'
                    : 'bg-yellow-600 hover:bg-yellow-700'
                }`}
              >
                {loading && lastActionType === 'pause' ? (
                  <>
                    <span className="hidden sm:inline">Pausing...</span>
                    <span className="sm:hidden">Pause...</span>
                  </>
                ) : (
                  'Pause'
                )}
              </button>
            )}
          </>
        ) : (
          <button
            onClick={handleStart}
            disabled={loading}
            className={`px-2 sm:px-3 py-1 text-white rounded transition-colors text-xs sm:text-sm font-medium min-w-[60px] sm:min-w-[70px] ${
              loading && lastActionType === 'start'
                ? 'bg-gray-500 cursor-not-allowed'
                : 'bg-green-600 hover:bg-green-700'
            }`}
          >
            {loading && lastActionType === 'start' ? (
              <>
                <span className="hidden sm:inline">Starting...</span>
                <span className="sm:hidden">Start...</span>
              </>
            ) : (
              'Start'
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default TerminalControls;